// src/pos-app/surfaces/waiter/lib/drawerFlow.js
//
// F12a-N Task W4 — the KDS-ticket-reconciliation engine behind the native
// order drawer (design contract: docs/mockups/f12a-n/waiter-tables.html,
// "Order drawer" section; inventory: docs/superpowers/specs/2026-07-15-
// f12a-n-appendix-waiter-kds-inventory.md, Surface A "Table tap → popover →
// drawer flow"). Faithful, cut-and-paste extraction from
// components/pos/WaiterOrderDrawer.js's submit/guard/tracking logic, same
// discipline as this surface's own lib/tableFlow.js and the cashier
// surface's lib/pendingOrderFlow.js. The legacy component is UNTOUCHED and
// keeps its own inline copies of every function below — it still backs the
// web Table Service page (TableServicePage.js) and WaiterStation's
// SURFACES.waiter='legacy' fallback. Only mechanical changes were made:
// closure-captured React state/props became explicit `ctx` parameters, same
// ctx pattern as every other lib/ file in this branch. No React imports
// here — params-for-closures only.
//
// Source-range table (legacy components/pos/WaiterOrderDrawer.js, current
// line numbers):
//   getDisplayName        <- 19-26  (employee_FirstLast -> "First Last";
//                            falls back to the raw username, then
//                            'Unknown')
//   buildTrackingRecords  <- 161-188 (mode:'new' only — one
//                            orderTrackingService.createOrderTracking call
//                            PER CATEGORY present in the order, each scoped
//                            to that category's item subset, with
//                            table_info + a staff-attributed notes string.
//                            A per-category failure is swallowed
//                            (console.error) so one bad category can't sink
//                            the rest of the ticket fan-out — deliberate,
//                            not a bug)
//   emitOrderEvents       <- 190-198 (cross-tab signal: one CustomEvent +
//                            localStorage set/remove pulse PER CATEGORY
//                            present in the cart. KDS does not listen to
//                            this — it only polls, inventory §Surface B —
//                            but other same-tab/other-tab windows use it to
//                            know an order landed)
//   submitOrder            <- 200-285 (handleSubmit), the full guarded
//                            submit:
//                              200-202 business-day guard (isBusinessDay &&
//                                businessDaySalesDate, same double-gate the
//                                legacy page's own useBusinessDayGuard
//                                re-checks at the point of action)
//                              204-206 table/products guard
//                              207-209 append needs an existing order id
//                              210-212 mode:'new' BLOCKED once
//                                occupation.first_order_at is already set
//                                (table already has a first order — must go
//                                through 'append' instead)
//                              219-236 append branch: build the updated
//                                order shape, tenantStorage.saveOrder(...,
//                                isNew=false, isEdit=true,
//                                existingOrder.order_id, salesDate), THEN
//                                utils/OrderHelpers.updateOrderTrackingRecords
//                                diffs originalProducts (captured at
//                                drawer-open, BEFORE any edits — see
//                                originalProducts in ctx) vs the current
//                                cart to patch/void/add KDS rows
//                                incrementally and flip is_edited (failure
//                                swallowed — the order itself is already
//                                saved by this point), THEN emitOrderEvents
//                                'order_updated'
//                              238-273 new branch:
//                                deductStockForPendingOrder FIRST — failure
//                                ABORTS the whole submit before anything is
//                                persisted (240-243) -> build the
//                                pending-order shape -> tenantStorage.
//                                saveOrder(isNew=false, isEdit=false) ->
//                                buildTrackingRecords (one KDS ticket per
//                                category) -> emitOrderEvents 'new_order' ->
//                                occupationService.markFirstOrder
//                                (best-effort, only once, only when not
//                                already set)
//                              276-279 both branches: refreshBulkStock,
//                                onOrderPlaced, close
//                              280-284 any thrown error anywhere in the try
//                                is caught and surfaced as ONE generic
//                                t('failed_to_submit_order') — legacy does
//                                not distinguish which step failed, kept
//                                byte-faithful (not "improved")
//
// Every value/setter the legacy closure captured (tenantConfig, username,
// diningOptionName, tableNumber, occupation, existingOrder, selectedProducts,
// notes, totals, addServiceCharge, addTax, the originalProducts snapshot
// (legacy: a ref, here: its .current value passed in explicitly), isBusinessDay/
// businessDaySalesDate, t, setError/setSubmitting, refreshBulkStock,
// onOrderPlaced, onClose) is passed via a single `ctx` object — the only
// structural change from the legacy source.
import { format } from 'date-fns';
import { orderTrackingService, occupationService } from '../../../../services/api';
import tenantStorage from '../../../../services/tenantStorage';
import {
    deductStockForPendingOrder,
    deductStockForAddedItems,
    updateOrderTrackingRecords,
} from '../../../../utils/OrderHelpers';
import { startTicketPrint } from '../../../../utils/ticketPrintDispatch';

// ---------------------------------------------------------------------------
// getDisplayName — legacy WaiterOrderDrawer.js:19-26 (verbatim).
// ---------------------------------------------------------------------------
export const getDisplayName = (username) => {
    if (username && username.startsWith('employee_')) {
        const name = username.replace('employee_', '');
        return name.replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/^[a-z]/, (m) => m.toUpperCase());
    }
    return username || 'Unknown';
};

// ---------------------------------------------------------------------------
// buildTrackingRecords — legacy WaiterOrderDrawer.js:161-188 (verbatim).
// mode:'new' only — creates the initial KDS ticket fan-out, one row per
// category present in `order.products`.
// ---------------------------------------------------------------------------
export const buildTrackingRecords = async (order, staffName, ctx) => {
    const { diningOptionName, tableNumber, tenantConfig, printableCategories } = ctx;
    const allCategories = [...new Set(order.products.map((p) => p.category))];
    // Whole-branch review, finding 3: the tracking-record fan-out below
    // stays over ALL categories — the KDS must show every category, whether
    // or not the tenant prints it — but the ticket PRINT is filtered by the
    // tenant's printable categories, matching the cashier surface's
    // dispatchKitchenTickets (which only ever loops over them). Without
    // this, a waiter-submitted order printed a ticket for a category the
    // same order, charged through the cashier surface, would not.
    // ctx.printableCategories is what submitOrder already threads through
    // (it passes the same value to updateOrderTrackingRecords); the
    // tenantConfig read is the fallback for any caller that omits it.
    const printable = printableCategories || tenantConfig?.printable_categories || [];
    // Each category's KDS ticket is independent — fan them out concurrently
    // rather than blocking submit on N sequential round-trips. Errors stay
    // isolated per category so one failure never aborts the others.
    await Promise.all(allCategories.map(async (category) => {
        const categoryItems = order.products.filter((p) => p.category === category);
        if (categoryItems.length === 0) return;
        try {
            const created = await orderTrackingService.createOrderTracking({
                transaction_id: null,
                order_id: order.order_id,
                category,
                printed_categories: false,
                items: categoryItems.map((item) => ({
                    name: item.name, quantity: item.quantity,
                    notes: item.notes || null, add_ons: item.add_ons || [],
                })),
                table_info: {
                    dining_option: diningOptionName,
                    table_number: tableNumber,
                    staff_name: staffName,
                },
                total_items_count: categoryItems.reduce((sum, i) => sum + i.quantity, 0),
                notes: `Staff: ${staffName}${order.notes ? ` | ${order.notes}` : ''}`,
            });
            // Device Printer Config Task 8 (caller wiring) — same best-effort
            // ticket print as the cashier's dispatchKitchenTickets: never let a
            // print failure take down tracking-record creation or the submit.
            // Whole-branch review, finding 1(b): started, never awaited, so a
            // printer that is off or a backend cold start can never hold the
            // waiter's submit open (see utils/ticketPrintDispatch.js).
            if (created?.tracking_id && printable.includes(category)) {
                startTicketPrint(category, created.tracking_id, tenantConfig);
            }
        } catch (e) {
            console.error(`Failed to create tracking for ${category}:`, e);
        }
    }));
};

// ---------------------------------------------------------------------------
// emitOrderEvents — legacy WaiterOrderDrawer.js:190-198 (verbatim). One
// cross-tab pulse per category present in the submitted product list.
// ---------------------------------------------------------------------------
export const emitOrderEvents = (submittedProducts, orderId, orderType) => {
    const cats = [...new Set(submittedProducts.map((p) => p.category))];
    cats.forEach((category) => {
        const eventData = { type: 'orderSubmitted', category, orderType, orderId, timestamp: Date.now() };
        localStorage.setItem('orderEvent', JSON.stringify(eventData));
        localStorage.removeItem('orderEvent');
        window.dispatchEvent(new CustomEvent('orderEvent', { detail: eventData }));
    });
};

// ---------------------------------------------------------------------------
// submitOrder — legacy WaiterOrderDrawer.js:200-285 (handleSubmit),
// verbatim guard order and branch bodies. Never throws — every failure path
// is handled internally (setError(+setSubmitting(false))), matching the
// legacy function exactly. `ctx.setSubmitting`/`ctx.setError` are the
// caller's React state setters; `ctx.originalProducts` is the append-mode
// pre-edit snapshot (legacy: originalProductsRef.current).
// ---------------------------------------------------------------------------
export const submitOrder = async (ctx) => {
    const {
        mode, isBusinessDay, businessDaySalesDate,
        diningOptionName, tableNumber, selectedProducts, notes, totals,
        addServiceCharge, addTax,
        occupation, existingOrder, originalProducts,
        tenantConfig, username, products, printableCategories,
        t, setError, setSubmitting,
        refreshBulkStock, onOrderPlaced, onClose,
    } = ctx;

    if (!isBusinessDay || !businessDaySalesDate) {
        setError(t('no_current_business_day_consult_manager'));
        return;
    }
    if (!diningOptionName || !tableNumber || selectedProducts.length === 0) {
        setError(t('please_select_table_and_add_products'));
        return;
    }
    if (mode === 'append' && !existingOrder?.order_id) {
        setError(t('order_id_not_found_refresh_page'));
        return;
    }
    if (mode === 'new' && occupation?.first_order_at) {
        setError(t('table_already_occupied'));
        return;
    }

    const tableKey = `${diningOptionName}-${tableNumber}`;
    const staffName = getDisplayName(username);

    try {
        setSubmitting(true);
        const salesDate = await tenantStorage.getState('salesDate');

        if (mode === 'append') {
            const updatedOrder = {
                ...existingOrder,
                products: selectedProducts,
                notes,
                subtotal: totals.subtotal,
                serviceCharge: totals.serviceCharge,
                tax: totals.tax,
                addServiceCharge,
                addTax,
                total: totals.total,
                time: format(new Date(), 'HH:mm:ss'),
            };
            await tenantStorage.saveOrder(tableKey, updatedOrder, false, true, existingOrder.order_id, salesDate);
            try {
                await updateOrderTrackingRecords(
                    originalProducts, selectedProducts, existingOrder.order_id,
                    existingOrder, tenantConfig, diningOptionName, tableKey,
                    staffName, products, false, { printableCategories },
                );
            } catch (e) { console.error('updateOrderTrackingRecords failed:', e); }

            // The items this append ADDED have to come off the shelf here.
            // The whole-order deduction belongs to the HOLD (the else branch
            // below), and a settled pending order now tells the server its
            // stock is already gone — so nothing downstream will deduct
            // them. Isolated for the same reason the tracking update above
            // is: the order is saved and the kitchen already has it.
            try {
                await deductStockForAddedItems(
                    originalProducts, selectedProducts, diningOptionName, tableNumber, username,
                );
            } catch (e) { console.error('deductStockForAddedItems failed:', e); }

            emitOrderEvents(selectedProducts, existingOrder.order_id, 'order_updated');
        } else {
            try {
                await deductStockForPendingOrder(selectedProducts, diningOptionName, tableNumber, username);
            } catch (e) {
                console.error('deductStockForPendingOrder failed:', e);
                setError(t('failed_to_deduct_stock_try_again'));
                setSubmitting(false);
                return;
            }
            const pendingOrder = {
                time: format(new Date(), 'HH:mm:ss'),
                date: format(new Date(), 'yyyy-MM-dd'),
                sales_date: salesDate,
                cashier: staffName,
                diningOption: diningOptionName,
                tableNumber,
                products: [...selectedProducts],
                subtotal: totals.subtotal,
                discount: 0,
                serviceCharge: totals.serviceCharge,
                total: totals.total,
                notes,
                paymentMethod: '',
                addServiceCharge,
                addTax,
                tax: totals.tax,
                is_submitted: false,
                is_printed: false,
            };
            const response = await tenantStorage.saveOrder(tableKey, pendingOrder, false, false, null, salesDate);
            const orderId = response.order_id;
            if (!orderId) throw new Error(t('failed_to_get_order_id'));
            await buildTrackingRecords({ ...pendingOrder, order_id: orderId }, staffName, ctx);
            emitOrderEvents(selectedProducts, orderId, 'new_order');
            // Move the occupation into the "ordered" lifecycle state.
            if (occupation?.id && !occupation.first_order_at) {
                try { await occupationService.markFirstOrder(occupation.id); }
                catch (e) { console.error('markFirstOrder failed:', e); }
            }
        }

        await refreshBulkStock();
        if (onOrderPlaced) await onOrderPlaced();
        setSubmitting(false);
        onClose();
    } catch (err) {
        console.error('Order submit failed:', err);
        setError(t('failed_to_submit_order'));
        setSubmitting(false);
    }
};
